import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import Header from "../../../../../components/common/header";
import clsx from "clsx";
import { getCDNResizedImageUrl } from "../../../../../utils/get-cdn-resized-image-url";
import { notify, notifyError } from "../../../../../utils/toast";

const GET_PRODUCT_WITH_MENU = gql`
  query GetProductWithMenu($id: Int!) {
    product(id: $id) {
      id
      name
      price
      discountPrice
      discountRate
      quantity
      description
      saleEndTime
      isSampleMenu
      menus {
        id
        img
        menuId
        quantity
        menu {
          name
          price
        }
      }
      store {
        id
        menus {
          id
          name
          price
          img
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_WITH_MENU = gql`
  mutation UpdateProductWithMenu($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
      name
      price
      discountPrice
      quantity
      description
      saleEndTime
      menus {
        menuId
        quantity
        img
      }
    }
  }
`;

interface Menu {
  name: string;
  price: number;
}

interface MenuItem {
  id: number;
  name: string;
  img: string;
  price: number;
  quantity: number; // 수량 필드 추가
  menu: Menu;
  menuId: number;
}

const SalesProductUpdate: React.FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const quantity = searchParams.get("quantity");
  const productId = id ? parseInt(id, 10) : null; // 안전한 변환
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<MenuItem[]>([]);
  const [discountRate, setDiscountRate] = useState(40);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isSampleMenu, setIsSampleMenu] = useState<boolean>(false); // 샘플 메뉴 상태 추가

  const discountRates = [30, 40, 50, 60]; // 사용할 할인율 목록

  // 미김펙 만자 불라오기
  const { data: dataProduct } = useQuery(GET_PRODUCT_WITH_MENU, {
    variables: { id: productId },
    skip: productId === null, // id가 없으면 쿼리 실행 안 함
  });

  useEffect(() => {
    containerRef.current?.scrollTo(0, 0); // 스크롤 초기화
  }, [containerRef]);

  // 마감팩의 메뉴 수량을 찾아서 매장 메뉴와 함께 저장
  useEffect(() => {
    if (dataProduct) {
      const dpMenus = dataProduct.product.menus;
      const dmMenus = dataProduct.product.store.menus;

      // dp의 menuId와 dm의 id가 일치하는 항목만 새로운 객체로 생성
      const newSelectedItems = dmMenus
        .filter((dm: any) => dpMenus.some((dp: any) => dp.menuId === dm.id))
        .map((dm: any) => {
          const matchingDp = dpMenus.find((dp: any) => dp.menuId === dm.id);
          return {
            ...dm,
            quantity: matchingDp.quantity, // 수량 불러오기
          };
        });

      setSelectedItems(newSelectedItems);

      setDiscountRate(dataProduct.product.discountRate); // 기존 할인율 불러오기
    }
  }, [dataProduct]);

  useEffect(() => {
    setIsSampleMenu(dataProduct?.product.isSampleMenu);
  }, [dataProduct]);

  // 마감팩 업데이트
  const [updateProduct] = useMutation(UPDATE_PRODUCT_WITH_MENU);

  const handleSelect = (menu: Omit<MenuItem, "quantity">) => {
    setSelectedItems(
      (prev) =>
        prev.some((item) => item?.id === menu?.id)
          ? prev.filter((item) => item?.id !== menu?.id)
          : [...prev, { ...menu, quantity: 1 }] // 기본 수량 1
    );
  };

  const handleDrop = (menu: Omit<MenuItem, "quantity">) => {
    setSelectedItems(
      (prev) =>
        prev.some((item) => item?.id === menu?.id)
          ? prev.map((item) =>
              item.id === menu.id ? { ...item, quantity: 0 } : item
            )
          : [...prev, { ...menu, quantity: 0 }] // 기본 수량 0
    );
  };

  const handleQuantityChange = (id: number, quantity: number) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item?.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const incrementQuantity = (id: number) => {
    handleQuantityChange(
      id,
      selectedItems.find((item) => item?.id === id)?.quantity! + 1
    );
  };

  const decrementQuantity = (id: number) => {
    const currentQuantity = selectedItems.find((item) => item?.id === id)
      ?.quantity!;
    if (currentQuantity > 1) {
      handleQuantityChange(id, currentQuantity - 1);
    }
  };

  const calculateTotalPrice = (pack: MenuItem[]): number => {
    return pack.reduce(
      (total, item) => total + item.price * (item.quantity || 1),
      0
    );
  };

  const calculateDiscountedPrice = (pack: MenuItem[]): number => {
    const totalPrice = calculateTotalPrice(pack);
    const discountPrice = totalPrice - totalPrice * (discountRate / 100);

    return discountPrice;
  };

  const handleComplete = async () => {
    if (selectedItems.length < 1) {
      alert("메뉴 수정 시 최소 한 개 이상의 메뉴를 선택해주세요!");
      return;
    }

    const packWithDiscount = selectedItems.map((item) => ({
      ...item,
      discountRate,
    }));

    const packName = packWithDiscount.map((item) => item.name).join(" + ");

    let menus: any = [];
    if (packWithDiscount.length > 0) {
      menus = packWithDiscount.map((item) => ({
        menuId: item.id,
        quantity: item.quantity || 1,
        img: item.img,
      }));
    }

    const menuPrevious = dataProduct.product.menus
      .map((menu: any) => `${menu.menu.name} ${menu.quantity}개`)
      .join("\n");

    const menuUpdated = selectedItems
      .map((item: any) => `${item.name} ${item.quantity}개`)
      .join("\n");

    const discountUpdated =
      dataProduct.product.discountRate !== discountRate
        ? `\n[할인율] ${dataProduct.product.discountRate}% -> ${discountRate}%`
        : "";

    const isConfirmed = window.confirm(
      `수정 사항을 저장할까요?\n\n[메뉴 수정 전]\n${menuPrevious}\n\n[메뉴 수정 후]\n${menuUpdated}\n${discountUpdated}`
    );

    if (!isConfirmed) {
      return; // 취소
    }

    const input = {
      id: dataProduct.product.id,
      name: packName,
      price: calculateTotalPrice(packWithDiscount),
      discountPrice: calculateDiscountedPrice(packWithDiscount),
      discountRate: discountRate,
      description: ``,
      saleEndTime: new Date().toISOString(),
      menus: menus, // menus를 그대로 전송 (배열 형태 유지)
      quantity: quantity ? parseInt(quantity, 10) : 1,
      img: undefined,
      isSampleMenu, // 샘플 메뉴 상태 추가
    };

    // 업데이트 실행
    await updateProduct({
      variables: {
        input,
      },
      onCompleted: () => {
        notify("수정되었습니다.");
      },
      onError: (error) => {
        notifyError(error.message);
      },
    });
    navigate("/boss/live-order/sales"); // 현재 판매 리스트로 돌아감
  };

  return (
    <div className="min-h-screen bg-white">
      <Header title="메뉴에서 선택 및 수정" showBackButton />
      <div ref={containerRef} className="p-6">
        <h2 className="text-2xl font-semibold mb-6">
          {dataProduct?.product.name}
        </h2>

        {dataProduct?.product.menus.length === 0 ? (
          <p className="text-gray-600">현재 등록된 메뉴가 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {dataProduct?.product.store.menus.map((menu: any) => (
              <div
                key={menu?.id}
                className={` border rounded-lg cursor-pointer ${
                  selectedItems.some((item) => item?.id === menu?.id)
                    ? "bg-blue-400 text-white"
                    : "bg-[#f3f3f3]"
                }`}
              >
                <div onClick={() => handleSelect(menu)} className="relative">
                  <img
                    src={getCDNResizedImageUrl(menu?.img, {
                      width: 1200,
                      quality: 80,
                    })}
                    alt={menu?.name}
                    className="w-full h-28 object-cover rounded-md mb-2"
                  />
                  <div className="p-2">
                    <div className="text-lg font-semibold">{menu?.name}</div>
                    <div className="text-md text-gray-800">
                      {menu?.price.toLocaleString()}원
                    </div>
                  </div>
                  {selectedItems.some((item) => item?.id === menu?.id) && (
                    <div
                      onClick={() => handleDrop(menu)}
                      className="absolute top-0 right-0 text-black bg-gray-100 text-4xl font-bold rounded-lg flex justify-center items-center h-9 w-9 border cursor-pointer"
                    >
                      &times;
                    </div>
                  )}
                </div>

                {selectedItems.some((item) => item?.id === menu?.id) && (
                  <div className="pt-1 flex flex-col items-center bg-white">
                    <label className="block text-sm font-semibold text-black">
                      수량
                    </label>
                    <div className="flex items-center space-x-2 pb-2 rounded">
                      <button
                        onClick={() => decrementQuantity(menu?.id)}
                        className="px-2 py-1 bg-gray-200 rounded"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={
                          selectedItems.find((item) => item?.id === menu?.id)
                            ?.quantity || 1
                        }
                        onChange={(e) =>
                          handleQuantityChange(menu?.id, Number(e.target.value))
                        }
                        className="w-16 p-2 border rounded-md text-black text-center"
                      />
                      <button
                        onClick={() => incrementQuantity(menu?.id)}
                        className="px-2 py-1 bg-gray-200 rounded"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <label className="block text-lg font-semibold mb-2">
            할인율 적용 (%)
          </label>
          <div className="flex space-x-4">
            {discountRates.map((rate) => (
              <button
                key={rate}
                onClick={() => setDiscountRate(rate)}
                type="button"
                className={clsx("px-4 py-2 border rounded-md", {
                  "bg-blue-500 text-white": discountRate === rate,
                  "bg-gray-200": discountRate !== rate,
                })}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-lg font-semibold mb-2">상품 유형</label>
          <div className="text-sm text-gray-500 mb-2">
            소비자에게 표시됩니다! 마감상품이 아닌데 마감상품으로 생각하면
            아쉽잖아요 😁
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={!isSampleMenu}
                onChange={(e) => setIsSampleMenu(!e.target.checked)}
                className="mr-2"
              />
              마감상품
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isSampleMenu}
                onChange={(e) => setIsSampleMenu(e.target.checked)}
                className="mr-2"
              />
              신상품 (홍보용, 신메뉴 테스트, 프로모션, 맛보기 등)
            </label>
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">선택된 메뉴</h3>
            <div className="flex flex-wrap gap-2">
              {selectedItems.map((menu) => (
                <div key={menu?.id} className="flex items-center space-x-4">
                  <img
                    src={getCDNResizedImageUrl(menu?.img, {
                      width: 1200,
                      quality: 80,
                    })}
                    alt={menu?.name}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                  <div>
                    <div className="text-black text-lg">{menu?.name}</div>
                    <div className="text-md text-gray-500">
                      {menu?.price}원 (수량: {menu?.quantity})
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <button
            onClick={handleComplete}
            className="w-full py-3 bg-[#1562fc] text-white rounded-md hover:bg-[#124ab7] transition duration-150"
          >
            완료하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesProductUpdate;
